// /condominios com o MESMO card de imóvel: aqui fica a tradução de um empreendimento
// (retorno de `empreendimentos_busca` / `getCompany`) para o `slide` que os 5 cards de
// imóvel já sabem desenhar, mais a prop `modoEmpreendimento` que os cards leem para
// trocar link/rótulo, esconder favorito/compartilhar/código e mostrar "A partir de".
//
// Regra: o slide leva SÓ o que o card precisa. Campos de imóvel (venda/aluguel/referencia/
// categoria/tipo/exibir_preco_*/area_privativa…) ficam de fora de propósito — `venda`
// acenderia o selo "Venda" e `area_privativa` duplicaria a área no cs1391.
import { removerAcentos } from './FunctionsGlobals';

export type ModoEmpreendimentoCard = {
    href: string;         // /empreendimento/{id} (+ ?codsite em host de teste)
    rotulo: string;       // title/alt: "Residencial X - Lançamento em Centro, Taubaté"
    precoPrefixo: string; // "A partir de"
    botao?: string;       // CTA no rodapé do card (BotaoVerEmpreendimento); sem valor = sem botão
};

export const PRECO_PREFIXO_EMPREENDIMENTO = 'A partir de';

// Rótulo único do CTA (decisão do Daniel 03/09, mantida no card novo em 16/09): o clique leva sempre a
// /empreendimento/{id}, e nem toda carteira é de apartamento.
export const CTA_EMPREENDIMENTO = 'Ver empreendimento';

// Mesmo parse do antigo ListPiecesCondominio: aceita "85,5", "85.5 m²"; sem número → undefined.
export const parseArea = (value: any): number | undefined => {
    if (value === undefined || value === null) return undefined;
    const normalized = String(value).replace(',', '.').replace(/[^\d.]/g, '');
    const parsed = Number(normalized);
    if (Number.isNaN(parsed) || parsed <= 0) return undefined;
    return parsed;
};

// Faixa do slider de preço: {min, max} sempre numéricos e max >= min (espelha o
// normalizePriceBounds de pages/imoveis/index.tsx).
export const normalizaFaixaPreco = (bounds: any): { min: number; max: number } => {
    const minValue = Number(bounds?.min);
    const maxValue = Number(bounds?.max);
    const normalizedMin = Number.isFinite(minValue) && minValue >= 0 ? minValue : 0;
    const normalizedMax = Number.isFinite(maxValue) && maxValue >= normalizedMin ? maxValue : normalizedMin;

    return {
        min: normalizedMin,
        max: normalizedMax,
    };
};

// Selo do card (vai em `mini_banner_texto`): mesma leitura do getMainChip antigo, agora
// sem acento para "Lançamento"/"lancamento" caírem no mesmo ramo.
export const getSeloEmpreendimento = (emp: any): string => {
    const categoria = String(emp?.categoria_empreendimento?.nome || '').trim();
    const situacao = String(emp?.situacao || '').trim();
    const merged = removerAcentos(`${categoria} ${situacao}`).toLowerCase();

    if (merged.includes('planta')) return 'Na planta';
    if (merged.includes('lanc')) return 'Lançamento';
    if (merged.includes('pronto')) return 'Pronto novo';
    return '';
};

const nomeCidadeDoEmpreendimento = (emp: any, cidades?: any[]): string => {
    const direto = typeof emp?.cidade === 'string' ? emp.cidade : emp?.cidade?.nome;
    if (direto) return String(direto).trim();
    if (emp?.id_cidade && Array.isArray(cidades)) {
        const cidade = cidades.find((city: any) => city?.id == emp?.id_cidade);
        return String(cidade?.nome || '').trim();
    }
    return '';
};

// "Residencial X - Lançamento em Centro, Taubaté" (title/alt do card).
export const rotuloEmpreendimento = (emp: any, cidades?: any[]): string => {
    const nome = String(emp?.nome || 'Empreendimento').trim();
    const selo = getSeloEmpreendimento(emp);
    const local = [String(emp?.bairro || '').trim(), nomeCidadeDoEmpreendimento(emp, cidades)]
        .filter(Boolean)
        .join(', ');

    return `${nome}${selo ? ` - ${selo}` : ''}${local ? ` em ${local}` : ''}`;
};

// Função pura: empreendimento → slide no formato que os cards de imóvel leem.
// Números ausentes saem `undefined` (nunca 0) para os cards não desenharem "0 vagas".
export const empreendimentoParaSlide = (emp: any, cidades?: any[]) => {
    const fotos = (Array.isArray(emp?.fotos) ? emp.fotos : []).filter((f: any) => f?.url_foto);
    // a API ordena por destaque DESC, posicao ASC; a query legacy (getCompany) nem traz `destaque`
    const capa = fotos.find((f: any) => f?.destaque === true || Number(f?.destaque) === 1) || fotos[0];
    const nome = String(emp?.nome || '').trim();
    const cidadeNome = nomeCidadeDoEmpreendimento(emp, cidades);
    const bairroNome = String(emp?.bairro || '').trim();
    const areaMin = parseArea(emp?.area_privativa_min ?? emp?.are_privativa_min);

    return {
        id: Number(emp?.id) || 0,
        titulo: nome,
        cidade: cidadeNome ? { nome: cidadeNome } : undefined,
        bairro: bairroNome ? { nome: bairroNome } : undefined,
        descricao: emp?.descricao || '',
        dormitorios: Number(emp?.dormitorios) || undefined,
        suites: Number(emp?.suites) || undefined,
        garagems: Number(emp?.vagas) || undefined,   // cards 1/2/4/horizontal
        vagas: Number(emp?.vagas) || undefined,      // CardApartament3
        area_total: areaMin || undefined,            // parseArea(area_privativa_min ?? are_privativa_min)
        preco_venda: Number(emp?.preco_min) || undefined,
        fotos: capa ? { url_foto: capa.url_foto } : undefined, // ramo slide.fotos.url_foto dos cards
        mini_banner_texto: getSeloEmpreendimento(emp) || undefined,
    };
};
